import React from "react";
import { Link } from "react-router-dom";
import { DollarSign, Users, Link as LinkIcon, FileText, Activity, Gauge, ArrowUpRight } from "lucide-react";
import { useNexOs, formatKpi } from "@/lib/nexOs";
import { useLabel, Section } from "@/lib/dynamicEngine";

const ICONS = {
  dollar: DollarSign,
  users: Users,
  link: LinkIcon,
  file: FileText,
  gauge: Gauge,
  default: Activity,
};

const ORDER = ["cash_on_hand", "credits_balance", "total_contacts", "active_previews", "recent_documents"];

/**
 * Second-row Ops KPI Pulse — platform-wide (not per-vertical). Sits between
 * the engine KPIs and the 10-step flow. Each card deep-links to the module
 * it summarizes, turning the row into a top-level navigation hub.
 */
export default function OpsKpiBar() {
  const { opsKpis } = useNexOs();
  const kicker = useLabel("nds.ops.kicker", "// PLATFORM OPS · CROSS-ENGINE");

  const cards = ORDER
    .map((id) => (opsKpis[id] ? { id, ...opsKpis[id] } : null))
    .filter(Boolean);

  return (
    <div className="flex flex-col gap-4" data-testid="nds-ops-bar">
      <div className="flex items-baseline justify-between">
        <div className="nds-label-dim">{kicker}</div>
        <div className="font-mono text-[0.7rem] text-nex-chrome tracking-[0.18em] uppercase">
          Every module · one glance
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {cards.map((c) => {
          const Icon = ICONS[c.icon] || ICONS.default;
          const Tag = c.href ? Link : "div";
          const linkProps = c.href ? { to: c.href } : {};
          return (
            <Section scope="nds_ops" id={c.id} key={c.id}>
              <Tag
                {...linkProps}
                data-testid={`nds-ops-card-${c.id}`}
                className={`nds-card p-5 relative no-underline text-nex-navy block ${c.href ? "hover:border-nex-violet hover:shadow-[0_18px_40px_rgba(124,92,255,0.20)] transition-all" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div className="nds-label">{c.label}</div>
                  <div className="w-9 h-9 grid place-items-center bg-nex-navy text-nex-goldbright rounded-sm">
                    <Icon className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                </div>
                <div
                  className="mt-4 text-3xl md:text-4xl nds-num tabular-nums"
                  data-testid={`nds-ops-value-${c.id}`}
                >
                  {formatKpi(c.value, c.format)}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span
                    className="nds-pill nds-pill--live"
                    style={{ padding: "3px 8px", fontSize: "0.6rem" }}
                  >
                    LIVE
                  </span>
                  {c.href && (
                    <span className="ml-auto inline-flex items-center gap-1 font-mono text-[0.6rem] tracking-[0.18em] uppercase text-nex-navy/50">
                      Open <ArrowUpRight className="w-3 h-3" />
                    </span>
                  )}
                </div>
              </Tag>
            </Section>
          );
        })}
        {cards.length === 0 && (
          <div className="col-span-5 nds-card p-8 text-center font-mono text-sm text-nex-chrome" data-testid="nds-ops-empty">
            Loading ops metrics…
          </div>
        )}
      </div>
    </div>
  );
}
